import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Play, Pause, RotateCcw, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTimer } from '@/contexts/TimerContext';

interface PomodoroTimerProps {
  compact?: boolean;
}

export function PomodoroTimer({ compact = false }: PomodoroTimerProps) {
  const {
    focusTime,
    breakTime,
    longBreakTime,
    cyclesUntilLongBreak,
    completedCycles,
    timeLeft,
    status,
    isLongBreak,
    nextBreakIsLong,
    setFocusTime,
    setBreakTime,
    setLongBreakTime,
    setCyclesUntilLongBreak,
    start,
    pause,
    resume,
    reset,
    startBreak,
    startLongBreak,
  } = useTimer();

  const [showSettings, setShowSettings] = useState(false);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const radius = compact ? 100 : 140;
  const svgSize = compact ? 224 : 320;
  const viewBox = compact ? '0 0 224 224' : '0 0 320 320';
  const center = compact ? 112 : 160;

  const progress = status === 'break'
    ? ((breakTime * 60 - timeLeft) / (breakTime * 60)) * 100
    : ((focusTime * 60 - timeLeft) / (focusTime * 60)) * 100;

  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    compact ? (
      <div className="p-2">{children}</div>
    ) : (
      <div className="rounded-lg border bg-card p-8">{children}</div>
    );

  return (
    <Wrapper>
      {/* Timer Display */}
      <div className={cn('relative flex items-center justify-center', compact ? 'mb-5' : 'mb-8')}>
        <svg
          className={cn('-rotate-90', compact ? 'w-56 h-56' : 'w-80 h-80')}
          viewBox={viewBox}
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted"
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            className={cn(
              'transition-all duration-1000',
              status === 'break' ? 'text-accent' : 'text-primary'
            )}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset,
            }}
          />
        </svg>

        <div className="absolute flex flex-col items-center">
          <span
            className={cn(
              'font-bold tabular-nums',
              compact ? 'text-5xl' : 'text-6xl',
              status === 'running' && 'animate-timer-glow'
            )}
          >
            {formatTime(timeLeft)}
          </span>
          <span className={cn('text-muted-foreground capitalize', compact ? 'mt-1 text-sm' : 'mt-2')}>
            {status === 'idle' && 'Pronto para começar'}
            {status === 'running' && 'Foco'}
            {status === 'paused' && 'Pausado'}
            {status === 'break' && (isLongBreak ? 'Pausa longa' : 'Pausa')}
            {status === 'finished' && 'Ciclo finalizado!'}
          </span>
        </div>
      </div>

      {/* Cycle Indicator */}
      <div
        className="flex items-center justify-center gap-1.5 mb-4"
        title={`${completedCycles % cyclesUntilLongBreak} de ${cyclesUntilLongBreak} ciclos até pausa longa`}
      >
        {Array.from({ length: cyclesUntilLongBreak }).map((_, i) => {
          const filledCount = completedCycles % cyclesUntilLongBreak === 0 && completedCycles > 0
            ? cyclesUntilLongBreak
            : completedCycles % cyclesUntilLongBreak;
          const filled = i < filledCount;
          return (
            <div
              key={i}
              className={cn(
                'h-2 w-2 rounded-full transition-colors',
                filled ? 'bg-primary' : 'bg-transparent border border-muted-foreground/40'
              )}
            />
          );
        })}
        <span className="ml-2 text-xs text-muted-foreground">
          {completedCycles % cyclesUntilLongBreak === 0 && completedCycles > 0
            ? cyclesUntilLongBreak
            : completedCycles % cyclesUntilLongBreak}
          /{cyclesUntilLongBreak}
        </span>
      </div>

      {/* Controls */}
      <div className={cn('flex items-center justify-center gap-4', compact ? 'mb-4' : 'mb-6')}>
        {status === 'idle' && (
          <Button variant="gradient" size={compact ? 'lg' : 'xl'} onClick={start}>
            <Play className="h-5 w-5 mr-2" />
            Iniciar
          </Button>
        )}

        {status === 'running' && (
          <>
            <Button variant="outline" size="lg" onClick={pause}>
              <Pause className="h-5 w-5 mr-2" />
              Pausar
            </Button>
            <Button variant="ghost" size="icon" onClick={reset}>
              <RotateCcw className="h-5 w-5" />
            </Button>
          </>
        )}

        {status === 'paused' && (
          <>
            <Button variant="gradient" size="lg" onClick={resume}>
              <Play className="h-5 w-5 mr-2" />
              Continuar
            </Button>
            <Button variant="ghost" size="icon" onClick={reset}>
              <RotateCcw className="h-5 w-5" />
            </Button>
          </>
        )}

        {status === 'finished' && (
          <div className="flex flex-col items-center gap-3">
            <div className="bg-accent/10 text-accent px-4 py-2 rounded-full font-medium text-sm">
              🎉 Ciclo concluído!
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {nextBreakIsLong ? (
                <Button variant="outline" size="sm" onClick={startLongBreak}>
                  Iniciar Pausa Longa ({longBreakTime}min)
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={startBreak}>
                  Iniciar Pausa ({breakTime}min)
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={reset}>
                Reiniciar
              </Button>
            </div>
          </div>
        )}

        {status === 'break' && (
          <Button variant="ghost" size="icon" onClick={reset}>
            <RotateCcw className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Settings Toggle */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
          className="text-muted-foreground"
        >
          <Settings2 className="h-4 w-4 mr-2" />
          Configurações
        </Button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mt-4 p-4 rounded-lg bg-muted/50 animate-fade-in">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="focus">Tempo de foco (min)</Label>
              <Input
                id="focus"
                type="number"
                min={1}
                max={120}
                value={focusTime}
                onChange={(e) => setFocusTime(Number(e.target.value))}
                disabled={status !== 'idle'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="break">Tempo de pausa (min)</Label>
              <Input
                id="break"
                type="number"
                min={1}
                max={30}
                value={breakTime}
                onChange={(e) => setBreakTime(Number(e.target.value))}
                disabled={status !== 'idle'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longBreak">Pausa longa (min)</Label>
              <Input
                id="longBreak"
                type="number"
                min={1}
                max={60}
                value={longBreakTime}
                onChange={(e) => setLongBreakTime(Number(e.target.value))}
                disabled={status !== 'idle'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cycles">Ciclos até pausa longa</Label>
              <Input
                id="cycles"
                type="number"
                min={2}
                max={10}
                value={cyclesUntilLongBreak}
                onChange={(e) => setCyclesUntilLongBreak(Number(e.target.value))}
                disabled={status !== 'idle'}
              />
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
}
